import { fetchChart } from "../network/chartFetch";

export interface PackDescriptor {
  /** Stable pack id; also the OPFS filename (`<id>.pmtiles`). */
  id: string;
  /** Style source this pack feeds: "s57", "hg" or "osm". */
  source: string;
  label: string;
  description: string;
  /** URL of the archive on its chart server. */
  file: string;
  bytes: number;
  /** WGS84 [west, south, east, north]. */
  bounds: [number, number, number, number];
  minzoom: number;
  maxzoom: number;
  tiles: number;
  /** Base packs are always installed; without them nothing renders. */
  required?: boolean;
  /** Optional compact name index installed and shared with this archive. */
  search?: SearchDescriptor;
}

export interface SearchDescriptor {
  /** URL of the compact JSON sidecar on the map server. */
  file: string;
  bytes: number;
  entries: number;
}

export interface PackManifest {
  version: string;
  generatedAt: string;
  style: string;
  packs: PackDescriptor[];
}

const MANIFEST_PATH = "packs/manifest.json";
const MANIFEST_CACHE_KEY = "klint.manifest";
const LOCAL_PACKS_KEY = "klint.localPacks";
const CHART_SERVER_KEY = "klint.mapServer";

export interface LoadManifestOptions {
  /** Override the saved chart server for this load. */
  server?: string | null;
  /** Set false when booting from installed/shared charts without network access. */
  allowNetwork?: boolean;
  /** Set false while validating a newly entered server address. */
  allowFallback?: boolean;
}

export function normaliseChartServer(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new Error("Enter the address of a map server.");
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    throw new Error("The map server must be a complete http:// or https:// address.");
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") {
    throw new Error("The map server must use HTTP or HTTPS.");
  }
  url.hash = "";
  url.search = "";
  return url.toString().replace(/\/$/, "");
}

export function chartServer(): string | null {
  const stored = localStorage.getItem(CHART_SERVER_KEY);
  if (!stored) return null;
  try {
    return normaliseChartServer(stored);
  } catch {
    return null;
  }
}

export function saveChartServer(server: string | null): string | null {
  if (!server?.trim()) {
    localStorage.removeItem(CHART_SERVER_KEY);
    return null;
  }
  const normalised = normaliseChartServer(server);
  localStorage.setItem(CHART_SERVER_KEY, normalised);
  return normalised;
}

export function cachedManifest(): PackManifest | null {
  return parseManifest(localStorage.getItem(MANIFEST_CACHE_KEY));
}

/**
 * Everything the browser already knows about, without contacting a server.
 * A valid empty catalogue is intentional: the map browser starts before the
 * user has chosen any source and gains maps later through its menu or sharing.
 */
export function localManifest(): PackManifest {
  return (
    mergeLocal(cachedManifest()) ?? {
      version: "local",
      generatedAt: "",
      style: "style.json",
      packs: [],
    }
  );
}

function localPacks(): PackDescriptor[] {
  const raw = localStorage.getItem(LOCAL_PACKS_KEY);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isPackDescriptor);
  } catch {
    return [];
  }
}

/** Keep descriptors for OPFS files even when a later server catalogue omits them. */
export function saveLocalPacks(packs: PackDescriptor[]): void {
  const merged = new Map(localPacks().map((pack) => [pack.id, pack]));
  for (const pack of packs) {
    if (!isPackDescriptor(pack)) throw new Error("The shared map description is invalid.");
    merged.set(pack.id, { ...pack });
  }
  localStorage.setItem(LOCAL_PACKS_KEY, JSON.stringify([...merged.values()]));
}

export function forgetLocalPack(packId: string): void {
  const remaining = localPacks().filter((pack) => pack.id !== packId);
  localStorage.setItem(LOCAL_PACKS_KEY, JSON.stringify(remaining));
}

function mergeLocal(manifest: PackManifest | null): PackManifest | null {
  const local = localPacks();
  if (!manifest && !local.length) return null;
  const packs = new Map((manifest?.packs ?? []).map((pack) => [pack.id, pack]));
  // The local descriptor describes the bytes actually in OPFS, so it wins if a
  // server later reuses the same id for a different archive. When the archive
  // still matches, however, allow a newer catalogue to enrich that retained
  // descriptor with search metadata added after the map was installed.
  for (const pack of local) {
    const catalogue = packs.get(pack.id);
    const sameArchive =
      catalogue?.bytes === pack.bytes && catalogue.source === pack.source;
    packs.set(
      pack.id,
      sameArchive
        ? { ...pack, search: catalogue.search ?? pack.search }
        : pack,
    );
  }
  return {
    version: manifest?.version ?? "shared",
    generatedAt: manifest?.generatedAt ?? new Date().toISOString(),
    style: manifest?.style ?? "style.json",
    packs: [...packs.values()],
  };
}

function parseManifest(raw: string | null): PackManifest | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as unknown;
    if (!value || typeof value !== "object") return null;
    const manifest = value as Partial<PackManifest>;
    if (!Array.isArray(manifest.packs) || !manifest.packs.every(isPackDescriptor)) return null;
    return {
      version: String(manifest.version ?? "unknown"),
      generatedAt: String(manifest.generatedAt ?? ""),
      style: String(manifest.style ?? "style.json"),
      packs: manifest.packs,
    };
  } catch {
    return null;
  }
}

/** Runtime validation for untrusted catalogues and peer-transfer metadata. */
export function isPackDescriptor(value: unknown): value is PackDescriptor {
  if (!value || typeof value !== "object") return false;
  const pack = value as Partial<PackDescriptor>;
  return (
    typeof pack.id === "string" &&
    /^[a-zA-Z0-9._-]{1,128}$/.test(pack.id) &&
    typeof pack.source === "string" &&
    pack.source.length > 0 &&
    pack.source.length <= 64 &&
    typeof pack.label === "string" &&
    pack.label.length > 0 &&
    pack.label.length <= 256 &&
    typeof pack.description === "string" &&
    typeof pack.file === "string" &&
    Number.isSafeInteger(pack.bytes) &&
    (pack.bytes ?? 0) > 0 &&
    Array.isArray(pack.bounds) &&
    pack.bounds.length === 4 &&
    pack.bounds.every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate)) &&
    typeof pack.minzoom === "number" &&
    typeof pack.maxzoom === "number" &&
    typeof pack.tiles === "number" &&
    (pack.search === undefined || isSearchDescriptor(pack.search))
  );
}

function isSearchDescriptor(value: unknown): value is SearchDescriptor {
  if (!value || typeof value !== "object") return false;
  const search = value as Partial<SearchDescriptor>;
  return (
    typeof search.file === "string" &&
    search.file.length > 0 &&
    Number.isSafeInteger(search.bytes) &&
    (search.bytes ?? 0) > 0 &&
    Number.isSafeInteger(search.entries) &&
    (search.entries ?? -1) >= 0
  );
}

function resolvePackUrls(manifest: PackManifest, server: string): PackManifest {
  const root = `${server}/`;
  return {
    ...manifest,
    packs: manifest.packs.map((pack) => ({
      ...pack,
      file: pack.file ? new URL(pack.file, root).toString() : "",
      search: pack.search
        ? { ...pack.search, file: new URL(pack.search.file, root).toString() }
        : undefined,
    })),
  };
}

/**
 * Fetch a catalogue from the user-selected server and retain a local copy.
 * Descriptors for files already in OPFS are merged in separately so the app
 * remains useful if the server disappears or never contained those charts.
 */
export async function loadManifest(
  options: LoadManifestOptions = {},
): Promise<PackManifest> {
  const server = options.server === undefined ? chartServer() : options.server;
  const allowNetwork = options.allowNetwork ?? true;
  const allowFallback = options.allowFallback ?? true;
  let networkError: unknown = null;

  if (server && allowNetwork) {
    try {
      const normalised = normaliseChartServer(server);
      const response = await fetchChart(new URL(MANIFEST_PATH, `${normalised}/`), {
        cache: "no-cache",
      });
      if (!response.ok) throw new Error(`server replied ${response.status}`);
      const parsed = parseManifest(await response.text());
      if (!parsed) throw new Error("the catalogue is not valid Klint manifest JSON");
      const manifest = resolvePackUrls(parsed, normalised);
      localStorage.setItem(MANIFEST_CACHE_KEY, JSON.stringify(manifest));
      return mergeLocal(manifest)!;
    } catch (error) {
      networkError = error;
    }
  }

  if (allowFallback) {
    const local = mergeLocal(cachedManifest());
    if (local) return local;
  }

  const detail = networkError instanceof Error ? ` (${networkError.message})` : "";
  const localNetworkHint =
    location.protocol === "https:" && server?.startsWith("http:")
      ? " Allow Klint local-network access when your browser asks. If it does not support HTTP local-network access, this server will need HTTPS."
      : "";
  throw new Error(
    server
      ? `Could not load a map catalogue from ${server}${detail}.${localNetworkHint}`
      : "No map server has been configured and there are no shared maps on this device.",
    networkError ? { cause: networkError } : undefined,
  );
}

export function packFileName(pack: PackDescriptor): string {
  return `${pack.id}.pmtiles`;
}

export function searchFileName(pack: PackDescriptor): string {
  return `${pack.id}.search.json`;
}

export function packTotalBytes(pack: PackDescriptor): number {
  return pack.bytes + (pack.search?.bytes ?? 0);
}

export function boundsIntersect(
  a: [number, number, number, number],
  b: [number, number, number, number],
): boolean {
  return !(a[2] < b[0] || a[0] > b[2] || a[3] < b[1] || a[1] > b[3]);
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${Math.round(bytes / 1e6)} MB`;
  if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} kB`;
  return `${bytes} B`;
}
